import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import theme from "theme";

import FormControlLabel from "components/FormControlLabel";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Stack from "components/Stack";
import Switch from "components/Switch";
import Text from "components/Text";
import toast from "components/Toast";

import { useSignedUser } from "entities/Auth/sdk";
import { useLoggedUserIsDealAdmin } from "entities/Deal/hooks";
import AddNewTeamButton from "entities/Team/components/AddNewTeamButton";
import DealAccessTable from "entities/Team/components/DealAccessTable";
import DealTeamExplanatoryBox from "entities/Team/components/DealTeamExplanatoryBox";
import DealTeamMemberInvitationsTable from "entities/Team/components/DealTeamMemberInvitationsTable";
import DealTeamSkeleton from "entities/Team/components/DealTeamSkeleton";
import PageTitleSkeleton from "entities/Team/components/WorkspacePageTitleSkeleton";
import {
  useDealTeamMemberInvitations,
  useDealTeamMembers,
} from "entities/Team/sdk";

const TeamMembers = () => {
  const [shouldGroupTeamMembersByCompany, setShouldGroupTeamMembersByCompany] =
    useState(false);
  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);
  const { data: dealTeamMembers, mutate: mutateDealTeamMembers } =
    useDealTeamMembers({ dealId });
  const {
    data: dealTeamMemberInvitations,
    mutate: mutateDealTeamMemberInvitations,
  } = useDealTeamMemberInvitations({
    dealId,
  });
  const { data: user } = useSignedUser();

  const { loggedUserIsDealAdmin } = useLoggedUserIsDealAdmin({ dealId });

  const dealTeamMemberGroups = useMemo(() => {
    if (shouldGroupTeamMembersByCompany) {
      return _.groupBy(
        dealTeamMembers,
        (teamMember) => teamMember.deal_company_permission.company.id
      );
    }

    const [
      dealTeamMembersFromTheSameCompany,
      dealTeamMembersFromDifferentCompanies,
    ] = _.partition(
      dealTeamMembers,
      (teamMember) =>
        teamMember.deal_company_permission.company.id === user?.company_id
    );

    return _.pickBy(
      {
        "My company's users": dealTeamMembersFromTheSameCompany,
        "External users": dealTeamMembersFromDifferentCompanies,
      },
      (group) => !_.isEmpty(group)
    );
  }, [dealTeamMembers, shouldGroupTeamMembersByCompany, user]);

  const handleAddNewTeam = useCallback(() => {
    toast.successMessage("Invite has been sent.");
    mutateDealTeamMembers();
    mutateDealTeamMemberInvitations();
  }, [mutateDealTeamMembers, mutateDealTeamMemberInvitations]);

  return (
    <Paper
      sx={{
        padding: theme.spacing(0, 4, 1, 4),
        flex: 1,
      }}
    >
      {!dealTeamMembers && (
        <Stack spacing={3} style={{ flex: 1 }}>
          <PageTitleSkeleton />

          <Paper>
            <DealTeamExplanatoryBox />

            <Stack spacing={6}>
              {[1, 2, 3].map((index) => (
                <DealTeamSkeleton key={index} />
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}
      {dealTeamMembers && !_.isUndefined(dealTeamMemberInvitations) && (
        <Stack spacing={3} flex="1" sx={{ overflowX: "auto" }}>
          <PageHeader
            title="Deal Team"
            actions={
              <Stack direction="row" gap={2}>
                <FormControlLabel
                  label={
                    <Text variant="text2" sx={{ fontWeight: 500 }}>
                      Group users by company:
                    </Text>
                  }
                  control={
                    <Switch
                      checked={shouldGroupTeamMembersByCompany}
                      onChange={() =>
                        setShouldGroupTeamMembersByCompany(
                          (prevValue) => !prevValue
                        )
                      }
                      data-testid="group-users-toggle-button"
                    />
                  }
                  labelPlacement="start"
                />

                {loggedUserIsDealAdmin && (
                  <AddNewTeamButton dealId={dealId} onSave={handleAddNewTeam} />
                )}
              </Stack>
            }
          />

          <Paper>
            <DealTeamExplanatoryBox />

            {Object.keys(dealTeamMemberGroups).map((groupName: string) => (
              <Stack
                marginBottom={8}
                spacing={2}
                data-testid="deal-team-section"
              >
                <Text variant="h3" data-testid="deal-team-name">
                  {shouldGroupTeamMembersByCompany
                    ? dealTeamMemberGroups[groupName][0].deal_company_permission
                        .company.name
                    : groupName}
                </Text>
                <DealAccessTable
                  dealAccesses={dealTeamMemberGroups[groupName]}
                  loggedUserIsDealAdmin={loggedUserIsDealAdmin}
                  useLastLoginForActivytyStatus={
                    groupName === "My company's users"
                  }
                />
              </Stack>
            ))}

            {!_.isEmpty(dealTeamMemberInvitations) && (
              <Stack data-testid="invitations-section" spacing={2}>
                <Text variant="h3" data-testid="invitations-section-name">
                  Pending invitations
                </Text>
                <DealTeamMemberInvitationsTable
                  invitations={dealTeamMemberInvitations}
                  loggedUserIsDealAdmin={loggedUserIsDealAdmin}
                />
              </Stack>
            )}
          </Paper>
        </Stack>
      )}
    </Paper>
  );
};

export default TeamMembers;
